export type CompleteStatus = 'green' | 'yellow' | 'red'

export interface MilestoneColumnDef {
  key: string
  label: string
  groupKey: string
  groupLabel: string
  classification: 'Systematic' | 'Informational'
}

export interface StageGroupDef {
  key: string
  label: string
  columns: MilestoneColumnDef[]
}

export const STAGE_GROUPS: StageGroupDef[] = [
  {
    key: 'assortmentPlanning',
    label: 'Assortment Planning',
    columns: [
      { key: 'assortmentPlanApproved', label: 'Assortment Plan Approved' },
    ],
  },
  {
    key: 'skuSetUp',
    label: 'SKU Set Up',
    columns: [{ key: 'skuSetUpDue', label: 'SKU Set Up Due' }],
  },
  {
    key: 'cutPOs',
    label: 'Cut POs',
    columns: [{ key: 'posDue', label: 'POs Due' }],
  },
  {
    key: 'transportationToDC',
    label: 'Transportation to DC',
    columns: [
      {
        key: 'vendorBookedTransportation',
        label: 'Vendor Booked Transportation',
      },
      { key: 'vendorCargoReadyDate', label: 'Vendor Cargo Ready Date' },
      { key: 'containerAtGate', label: 'Container at Gate' },
      { key: 'vesselDeparture', label: 'Vessel Departure' },
      {
        key: 'shipmentArrivedAtPortOfDischarge',
        label: 'Shipment Arrived at Port of Discharge',
      },
      {
        key: 'unloadVesselDischargeDate',
        label: 'Unload Vessel / Discharge Date',
      },
      { key: 'railDeparture', label: 'Rail Departure' },
      { key: 'railArrival', label: 'Rail Arrival' },
      { key: 'shipmentPickup', label: 'Shipment Pickup' },
    ],
  },
  {
    key: 'spacePlanning',
    label: 'Space Planning',
    columns: [{ key: 'attachedToPOG', label: 'Attached to POG' }],
  },
  {
    key: 'dcProcessing',
    label: 'DC Processing',
    columns: [
      { key: 'productArrivedInbound', label: 'Product Arrived (Inbound)' },
      { key: 'productReceivedInbound', label: 'Product Received (Inbound)' },
    ],
  },
  {
    key: 'transportationToStores',
    label: 'Transportation to Stores',
    columns: [
      { key: 'shipToStoreOutbound', label: 'Ship to Store (Outbound)' },
    ],
  },
  {
    key: 'stores',
    label: 'Stores',
    columns: [{ key: 'inStoreDate', label: 'In Store Date' }],
  },
].map(group => ({
  ...group,
  columns: group.columns.map((col, i) => ({
    ...col,
    groupKey: group.key,
    groupLabel: group.label,
    classification: (group.key === 'assortmentPlanning' && i === 0
      ? 'Systematic'
      : 'Informational') as 'Systematic' | 'Informational',
  })),
}))

export const MILESTONE_COLUMNS: MilestoneColumnDef[] = STAGE_GROUPS.flatMap(
  g => g.columns
)

const OFFSET_DAYS = [
  0, 7, 28, 90, 96, 106, 113, 141, 146, 151, 152, 154, 157, 151, 156, 166, 176,
]

const DC_OPTIONS = [
  '1_Dallas',
  '2_Atlanta',
  '3_Memphis',
  '4_Indianapolis',
  '5_Los Angeles',
]
const IMPORT_DOMESTIC_OPTIONS = ['Import', 'Domestic']
const INITIAL_REPLEN_RESET_OPTIONS = ['Initial', 'Replen', 'Reset']
const POG_GROUP_SUFFIXES = [
  'Core Assortment',
  'Feature Assortment',
  'Value Assortment',
]
const LEAF_ROWS_PER_GROUP = 3

interface SeasonGroupProfile {
  key: string
  label: string
  startDate: string
  completionCurve: number[]
  forcedRedIndex?: number
  maxDelayDays: number
}

const SEASON_PROFILES: SeasonGroupProfile[] = [
  {
    key: 'Early Spring',
    label: 'Early Spring',
    startDate: '2025-01-26',
    completionCurve: [
      100, 100, 100, 100, 100, 100, 100, 100, 98, 98, 96, 95, 100, 100, 100, 93,
      89,
    ],
    forcedRedIndex: 3,
    maxDelayDays: 25,
  },
  {
    key: 'Winter',
    label: 'Winter',
    startDate: '2025-07-13',
    completionCurve: [100, 79, 10, 6, 8, 4, 1, 1, 1, 1, 1, 8, 1, 1, 1, 1, 1],
    maxDelayDays: 60,
  },
  {
    key: 'Fall',
    label: 'Fall',
    startDate: '2025-05-04',
    completionCurve: [
      100, 97, 96, 92, 93, 89, 87, 82, 78, 74, 70, 68, 74, 70, 65, 63, 50,
    ],
    maxDelayDays: 15,
  },
  {
    key: 'Summer',
    label: 'Summer',
    startDate: '2025-06-01',
    completionCurve: [
      72, 67, 63, 60, 58, 55, 52, 50, 48, 46, 44, 42, 60, 58, 53, 51, 33,
    ],
    maxDelayDays: 12,
  },
  {
    key: 'Spring',
    label: 'Spring',
    startDate: '2025-07-20',
    completionCurve: [
      73, 88, 86, 84, 72, 70, 67, 63, 60, 58, 55, 53, 70, 61, 58, 55, 42,
    ],
    maxDelayDays: 10,
  },
]

export const SEASON_OPTIONS = SEASON_PROFILES.map(p => p.key)

export interface MilestoneValue {
  expectedDate: Date
  projectedDate: Date
  completePct: number
  delayDays: number
  status: CompleteStatus
}

export interface LeafRow {
  id: string
  season: string
  event: string
  pogGroup: string
  pogId: string
  dc: string
  po: string
  sku: string
  importDomestic: string
  initialReplenReset: string
  unitsTotal: number
  milestones: Record<string, MilestoneValue>
}

export type EventProductFlowHierarchyLevel =
  | 'season'
  | 'event'
  | 'pogGroup'
  | 'pogId'
  | 'dc'
  | 'po'
  | 'sku'

export interface EventProductFlowHierarchyNode {
  key: string
  label: string
  level: EventProductFlowHierarchyLevel
  depth: number
  leafRows: LeafRow[]
  children: EventProductFlowHierarchyNode[]
  aggregate: Record<string, MilestoneValue>
}

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function normalizedFromHash(seed: number, salt: string): number {
  return (hashString(`${seed}:${salt}`) % 10000) / 10000
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + Math.round(days))
  return next
}

export function formatShortDate(date: Date): string {
  return `${date.getMonth() + 1}/${date.getDate()}`
}

export function formatFullDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  })
}

function computeStatus(completePct: number, delayDays: number): CompleteStatus {
  const effectiveDelay = Math.max(0, delayDays)
  if (completePct >= 95 && effectiveDelay <= 3) return 'green'
  if (completePct >= 90 && effectiveDelay <= 7) return 'yellow'
  return 'red'
}

function buildMilestoneValue(
  startDate: Date,
  columnIndex: number,
  basePct: number,
  maxDelayDays: number,
  forced: boolean,
  seed: number
): MilestoneValue {
  const jitter = (normalizedFromHash(seed, `pct${columnIndex}`) - 0.5) * 10 // +-5pts
  const completePct = Math.min(100, Math.max(0, Math.round(basePct + jitter)))

  const expectedDate = addDays(startDate, OFFSET_DAYS[columnIndex])

  let delayDays = forced
    ? maxDelayDays
    : Math.round(((100 - completePct) / 100) * maxDelayDays)
  const delayJitter = Math.round(
    (normalizedFromHash(seed, `delay${columnIndex}`) - 0.5) * 4
  )
  delayDays = Math.max(-3, delayDays + delayJitter)

  const projectedDate = addDays(expectedDate, delayDays)
  const status = computeStatus(completePct, delayDays)

  return { expectedDate, projectedDate, completePct, delayDays, status }
}

function buildLeafRow(profile: SeasonGroupProfile, index: number): LeafRow {
  const id = `${profile.key}-${index}`
  const seed = hashString(id)
  const startDate = new Date(profile.startDate)

  const milestones: Record<string, MilestoneValue> = {}
  let previousProjected: Date | null = null
  MILESTONE_COLUMNS.forEach((col, colIndex) => {
    const value = buildMilestoneValue(
      startDate,
      colIndex,
      profile.completionCurve[colIndex],
      profile.maxDelayDays,
      profile.forcedRedIndex === colIndex,
      seed
    )
    // A projection can't land before the projection of any earlier step.
    if (previousProjected && value.projectedDate < previousProjected) {
      value.projectedDate = previousProjected
      value.delayDays = Math.round(
        (previousProjected.getTime() - value.expectedDate.getTime()) / 86400000
      )
      value.status = computeStatus(value.completePct, value.delayDays)
    }
    previousProjected = value.projectedDate
    milestones[col.key] = value
  })

  const dcIndex = Math.floor(normalizedFromHash(seed, 'dc') * DC_OPTIONS.length)
  const importIndex = Math.floor(
    normalizedFromHash(seed, 'import') * IMPORT_DOMESTIC_OPTIONS.length
  )
  const initialIndex = Math.floor(
    normalizedFromHash(seed, 'initial') * INITIAL_REPLEN_RESET_OPTIONS.length
  )

  return {
    id,
    season: profile.key,
    event: `SEA${String(hashString(id + 'event') % 30).padStart(2, '0')}`,
    pogGroup: `${profile.label} ${POG_GROUP_SUFFIXES[index % POG_GROUP_SUFFIXES.length]}`,
    pogId: `POG-${1000 + (hashString(id + 'pog') % 9000)}`,
    dc: DC_OPTIONS[dcIndex],
    po: String(40000000 + (hashString(id + 'po') % 9999999)),
    sku: String(10000000 + (hashString(id + 'sku') % 89999999)),
    importDomestic: IMPORT_DOMESTIC_OPTIONS[importIndex],
    initialReplenReset: INITIAL_REPLEN_RESET_OPTIONS[initialIndex],
    unitsTotal: 200 + (hashString(id + 'units') % 1300),
    milestones,
  }
}

export const ALL_LEAF_ROWS: LeafRow[] = SEASON_PROFILES.flatMap(profile =>
  Array.from({ length: LEAF_ROWS_PER_GROUP }, (_, i) =>
    buildLeafRow(profile, i)
  )
)

export const YEAR_EVENT_OPTIONS = Array.from(
  new Set(ALL_LEAF_ROWS.map(r => r.event))
).sort()
export const DC_GROUP_OPTIONS = Array.from(
  new Set(ALL_LEAF_ROWS.map(r => r.dc))
).sort()
export const PO_OPTIONS = Array.from(
  new Set(ALL_LEAF_ROWS.map(r => r.po))
).sort()
export const SKU_OPTIONS = Array.from(
  new Set(ALL_LEAF_ROWS.map(r => r.sku))
).sort()
export const POG_GROUP_OPTIONS = Array.from(
  new Set(ALL_LEAF_ROWS.map(r => r.pogId))
).sort()

export const MILESTONES_DISPLAY_OPTIONS = [
  'All Milestones',
  'Key Milestones Only',
]
export const MILESTONE_CLASSIFICATION_OPTIONS = ['Systematic', 'Informational']

const KEY_MILESTONE_KEYS = new Set([
  'assortmentPlanApproved',
  'vendorBookedTransportation',
  'attachedToPOG',
  'shipToStoreOutbound',
  'inStoreDate',
])

export interface EventProductFlowFilterState {
  season: string | null
  yearEvent: string | null
  milestonesDisplay: string | null
  milestoneClassification: string | null
  dcGroupDcName: string | null
  po: string | null
  department: string | null
  classSubclass: string | null
  importDomestic: string | null
  initialReplenReset: string | null
  pogGroupPogId: string | null
  sku: string | null
}

function cosmeticFilterPairs(
  filters: EventProductFlowFilterState
): Array<[string, string]> {
  const pairs: Array<[string, string]> = []
  if (filters.department) pairs.push(['dept', filters.department])
  if (filters.classSubclass)
    pairs.push(['classSubclass', filters.classSubclass])
  return pairs
}

function applyCosmeticWiggle(
  value: MilestoneValue,
  wiggleSeed: number,
  columnKey: string
): MilestoneValue {
  if (wiggleSeed === 0) return value
  const noise = (normalizedFromHash(wiggleSeed, columnKey) - 0.5) * 6 // +-3pts
  const completePct = Math.min(
    100,
    Math.max(0, Math.round(value.completePct + noise))
  )
  const status = computeStatus(completePct, value.delayDays)
  return { ...value, completePct, status }
}

export function visibleMilestoneColumns(
  filters: EventProductFlowFilterState
): MilestoneColumnDef[] {
  let columns = MILESTONE_COLUMNS
  if (filters.milestonesDisplay === 'Key Milestones Only') {
    columns = columns.filter(c => KEY_MILESTONE_KEYS.has(c.key))
  }
  if (filters.milestoneClassification) {
    columns = columns.filter(
      c => c.classification === filters.milestoneClassification
    )
  }
  return columns
}

function matchesRowFilters(
  row: LeafRow,
  filters: EventProductFlowFilterState
): boolean {
  if (filters.season && row.season !== filters.season) return false
  if (filters.yearEvent && row.event !== filters.yearEvent) return false
  if (filters.dcGroupDcName && row.dc !== filters.dcGroupDcName) return false
  if (filters.po && row.po !== filters.po) return false
  if (filters.pogGroupPogId && row.pogId !== filters.pogGroupPogId) return false
  if (filters.sku && row.sku !== filters.sku) return false
  if (filters.importDomestic && row.importDomestic !== filters.importDomestic)
    return false
  if (
    filters.initialReplenReset &&
    row.initialReplenReset !== filters.initialReplenReset
  )
    return false
  return true
}

function averageAggregate(
  rows: LeafRow[],
  columns: MilestoneColumnDef[]
): Record<string, MilestoneValue> {
  const aggregate: Record<string, MilestoneValue> = {}
  columns.forEach(col => {
    const values = rows.map(r => r.milestones[col.key])
    const avgPct = Math.round(
      values.reduce((sum, v) => sum + v.completePct, 0) / values.length
    )
    const avgDelay = Math.round(
      values.reduce((sum, v) => sum + v.delayDays, 0) / values.length
    )
    const expectedDate = values.reduce(
      (min, v) => (v.expectedDate < min ? v.expectedDate : min),
      values[0].expectedDate
    )
    const projectedDate = values.reduce(
      (max, v) => (v.projectedDate > max ? v.projectedDate : max),
      values[0].projectedDate
    )
    aggregate[col.key] = {
      expectedDate,
      projectedDate,
      completePct: avgPct,
      delayDays: avgDelay,
      status: computeStatus(avgPct, avgDelay),
    }
  })
  return aggregate
}

const HIERARCHY_LEVELS: EventProductFlowHierarchyLevel[] = [
  'season',
  'event',
  'pogGroup',
  'pogId',
  'dc',
  'po',
  'sku',
]

function buildHierarchyNodes(
  rows: LeafRow[],
  columns: MilestoneColumnDef[],
  depth = 0,
  parentPath: Array<[EventProductFlowHierarchyLevel, string]> = []
): EventProductFlowHierarchyNode[] {
  const level = HIERARCHY_LEVELS[depth]
  const groupedRows = new Map<string, LeafRow[]>()

  rows.forEach(row => {
    const label = row[level]
    const existingRows = groupedRows.get(label)
    if (existingRows) existingRows.push(row)
    else groupedRows.set(label, [row])
  })

  return Array.from(groupedRows, ([label, descendantRows]) => {
    const path: Array<[EventProductFlowHierarchyLevel, string]> = [
      ...parentPath,
      [level, label],
    ]
    const isLeafLevel = depth === HIERARCHY_LEVELS.length - 1

    return {
      key: JSON.stringify(path),
      label,
      level,
      depth,
      leafRows: descendantRows,
      children: isLeafLevel
        ? []
        : buildHierarchyNodes(descendantRows, columns, depth + 1, path),
      aggregate: averageAggregate(descendantRows, columns),
    }
  })
}

export function buildSeasonGroups(
  filters: EventProductFlowFilterState
): EventProductFlowHierarchyNode[] {
  const columns = visibleMilestoneColumns(filters)
  const cosmeticPairs = cosmeticFilterPairs(filters)
  const wiggleSeed = cosmeticPairs.length
    ? hashString(cosmeticPairs.map(([n, v]) => `${n}=${v}`).join('|'))
    : 0

  const filteredRows = ALL_LEAF_ROWS.filter(row =>
    matchesRowFilters(row, filters)
  )

  const wiggledRows: LeafRow[] = filteredRows.map(row => {
    const milestones: Record<string, MilestoneValue> = {}
    columns.forEach(col => {
      milestones[col.key] = applyCosmeticWiggle(
        row.milestones[col.key],
        wiggleSeed,
        `${row.id}:${col.key}`
      )
    })
    return { ...row, milestones }
  })

  return buildHierarchyNodes(wiggledRows, columns)
}

export interface ExportDetailRow {
  id: string
  season: string
  event: string
  pogGroup: string
  dc: string
  po: string
  sku: string
  importDomestic: string
  initialReplenReset: string
  milestoneClassification: 'Systematic' | 'Informational'
  milestoneGroup: string
  milestoneName: string
  milestoneOrder: number
  expectedDate: Date
  projectedDate: Date
  delayDays: number
  unitsTotal: number
}

export function buildExportRows(
  filters: EventProductFlowFilterState
): ExportDetailRow[] {
  const groups = buildSeasonGroups(filters)
  const columns = visibleMilestoneColumns(filters)
  const rows: ExportDetailRow[] = []

  groups.forEach(group => {
    group.leafRows.forEach(row => {
      columns.forEach((col, index) => {
        const value = row.milestones[col.key]
        rows.push({
          id: `${row.id}-${col.key}`,
          season: row.season,
          event: row.event,
          pogGroup: row.pogGroup,
          dc: row.dc,
          po: row.po,
          sku: row.sku,
          importDomestic: row.importDomestic,
          initialReplenReset: row.initialReplenReset,
          milestoneClassification: col.classification,
          milestoneGroup: col.groupLabel,
          milestoneName: col.label,
          milestoneOrder: index + 1,
          expectedDate: value.expectedDate,
          projectedDate: value.projectedDate,
          delayDays: value.delayDays,
          unitsTotal: row.unitsTotal,
        })
      })
    })
  })

  return rows
}

export const STATUS_CELL_CLASSES: Record<CompleteStatus, string> = {
  green: 'bg-green-100',
  yellow: 'bg-amber-100',
  red: 'bg-rose-100',
}
